import { useState } from 'react';
import Layout from '../components/Layout';

type Platform = 'macos' | 'linux' | 'windows';

// Downloads page shows a NON-functional preview of the install command
// (with <token> placeholder). The real, copy-paste-ready command is on
// the Devices page where we have the user's actual token. We deliberately
// don't render a "Copy" button here so users can't paste a broken command.

export default function DownloadsPage() {
	const [platform, setPlatform] = useState<Platform>('macos');

	const platforms: { id: Platform; label: string; icon: JSX.Element }[] = [
		{
			id: 'macos',
			label: 'macOS',
			icon: (
				<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
					<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
				</svg>
			),
		},
		{
			id: 'linux',
			label: 'Linux',
			icon: (
				<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
					<path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.065 1.66-.529 1.735-1.481.047-.596-.24-1.075-.56-1.492-.325-.427-.662-.849-.727-1.339-.062-.476.109-.801.234-1.298a8.548 8.548 0 00.241-2.183c-.006-.14-.032-.27-.046-.399-.22-1.17-.588-2.146-1.29-2.98a5.097 5.097 0 00-1.097-1.093 4.876 4.876 0 00-.247-3.455A3.456 3.456 0 0012.504 0zm-.126 2.646a1.866 1.866 0 011.259.482c.354.273.563.685.636 1.15a3.026 3.026 0 00-.608-.024c-.366.025-.763.13-1.14.36a.456.456 0 01-.066-.173c-.041-.251-.155-.46-.268-.66-.097-.18-.183-.353-.19-.534a2.126 2.126 0 01.377-.601z" />
				</svg>
			),
		},
		{
			id: 'windows',
			label: 'Windows',
			icon: (
				<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
					<path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
				</svg>
			),
		},
	];

	return (
		<Layout title="Downloads" subtitle="Get the ClawdGate connector and mobile apps">
			<div className="max-w-4xl mx-auto space-y-8">
				{/* Connector / Agent */}
				<div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
					<div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-xl flex items-center justify-center">
								<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
								</svg>
							</div>
							<div>
								<h2 className="text-lg font-semibold text-slate-900 dark:text-white">ClawdGate Connector</h2>
								<p className="text-sm text-slate-500 dark:text-slate-400">Connects your AI agents to ClawdGate for security, monitoring, and management.</p>
							</div>
						</div>
					</div>
					<div className="p-6">
						{/* Platform tabs */}
						<div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-5">
							{platforms.map((p) => (
								<button
									key={p.id}
									onClick={() => setPlatform(p.id)}
									className={`flex items-center gap-2 flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
										platform === p.id
											? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
											: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
									}`}
								>
									<span className={platform === p.id ? 'text-primary-600 dark:text-primary-400' : ''}>{p.icon}</span>
									{p.label}
								</button>
							))}
						</div>

						{/* Install command preview (without token — the real one is on the Devices page) */}
						<div className="relative mb-4">
							<pre className="bg-slate-900 dark:bg-black text-slate-300 p-4 rounded-xl font-mono text-sm overflow-x-auto">
								{platform === 'windows'
									? '$env:CLAWDGATE_TOKEN="<token>"; irm https://download.clawdgate.com/install.ps1 | iex'
									: 'curl -sSL https://download.clawdgate.com/install.sh | CLAWDGATE_TOKEN=<token> sh'}
							</pre>
						</div>

						<p className="text-xs text-slate-500 dark:text-slate-400">
							{platform === 'windows'
								? 'Run in PowerShell as Administrator. Replace <token> with your registration token.'
								: <>Replace <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">&lt;token&gt;</code> with your registration token. Installs to <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">~/.local/bin</code>, no sudo needed.</>
							}
						</p>

						<div className="mt-5 pt-5 border-t border-slate-200 dark:border-slate-800">
							<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
								The install command needs your registration token — get it from the{' '}
								<a href="/machines" className="text-primary-600 dark:text-primary-400 hover:underline">Devices page</a>.
								The Add Device panel there has the full one-shot command with the token already embedded.
							</p>
						</div>
					</div>
				</div>

				{/* Mobile Apps */}
				<div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
					<div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800">
						<div className="flex items-center gap-3">
							<div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
								<svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
								</svg>
							</div>
							<div>
								<h2 className="text-lg font-semibold text-slate-900 dark:text-white">Mobile Apps</h2>
								<p className="text-sm text-slate-500 dark:text-slate-400">Manage your AI workspaces on the go — chat, monitor devices, and control security.</p>
							</div>
						</div>
					</div>
					<div className="p-6">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* iOS */}
							<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6">
								<div className="flex items-center gap-3 mb-4">
									<div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center">
										<svg className="w-5 h-5 text-slate-700 dark:text-slate-300" viewBox="0 0 24 24" fill="currentColor">
											<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
										</svg>
									</div>
									<div>
										<h3 className="text-base font-semibold text-slate-900 dark:text-white">iOS</h3>
										<p className="text-xs text-slate-500 dark:text-slate-400">iPhone & iPad</p>
									</div>
								</div>
								<a
									href="https://apps.apple.com/us/app/clawdgate/id6758634972"
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
								>
									<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
										<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
									</svg>
									Download on App Store
								</a>
								<div className="mt-4">
									<img src="/img/clawdgate-ios-qr.png" alt="Scan to download on App Store" className="w-24 h-24 rounded-lg border border-slate-200 dark:border-slate-700" />
								</div>
							</div>

							{/* Android */}
							<div className="border border-slate-200 dark:border-slate-800 rounded-xl p-6">
								<div className="flex items-center gap-3 mb-4">
									<div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
										<svg className="w-5 h-5 text-green-600 dark:text-green-400" viewBox="0 0 24 24" fill="currentColor">
											<path d="M17.523 15.341a.96.96 0 0 0-.953.953c0 .533.42.954.953.954a.96.96 0 0 0 .954-.954.96.96 0 0 0-.954-.953zm-11.046 0a.96.96 0 0 0-.954.953c0 .533.42.954.954.954a.96.96 0 0 0 .953-.954.96.96 0 0 0-.953-.953zm11.4-6.127l1.998-3.464a.416.416 0 0 0-.152-.567.416.416 0 0 0-.568.152l-2.024 3.51A12.17 12.17 0 0 0 12 7.876c-1.82 0-3.542.39-5.131.969L4.845 5.335a.416.416 0 0 0-.568-.152.416.416 0 0 0-.152.567L6.123 9.214C2.702 11.01.498 14.173.498 17.83h23.004c0-3.657-2.204-6.82-5.625-8.616z" />
										</svg>
									</div>
									<div>
										<h3 className="text-base font-semibold text-slate-900 dark:text-white">Android</h3>
										<p className="text-xs text-slate-500 dark:text-slate-400">APK Download</p>
									</div>
								</div>
								<a
									href="https://download.clawdgate.com/mobile/android/clawdgate-latest.apk"
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
								>
									<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
									</svg>
									Download APK
								</a>
								<div className="mt-4">
									<img src="/img/clawdgate-droid-qr.png" alt="Scan to download Android APK" className="w-24 h-24 rounded-lg border border-slate-200 dark:border-slate-700" />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
